import { message } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import OtpInput from "react-otp-input";
import usePostData from "../../api/usePostData";
import Button from "../../components/Button/Button";
import { getNewTime, resetExpiration } from "../../utils/getOTPExpirationTime";
import Heading from "../Heading/Heading";
import { RegisterTracking } from "../../utils/mixpanel";

/**
 * A component that represents the otp step form of the registeration process.
 * It is stored in a separate component because there are two different places where the registeration takes place with some differences in each.
 * It is intended for this component to not call the useContext hook directly, but for its parent to do so instead.
 *
 * @param {Object} props
 * @param {string} props.email - The submitted value of the email
 * @param {(otp: string)=>void} props.finishOTP - A callback function to be called when the otp submission is successfull
 * @param {()=>void} props.unsetEmail - A function the unsets the email to return the user to the previous step
 * @param {number} props.timer - The current value of the OTP timer
 * @param {(value: React.SetStateAction<number>)=>void} props.setTimer - A set state for the timer
 * @returns {JSX.Element}
 */

const OTPStepForm = ({ email, finishOTP, unsetEmail, timer, setTimer }) => {
  const { postData, loading: sendingRequest, errors } = usePostData();
  const [error, setError] = useState("");
  const [showResend, setShowResend] = useState(false); // State to show resend button
  const { t } = useTranslation("global");
  const [otp, setOtp] = useState("");

  const handleChange = (value) => {
    setOtp(value);
    setError(""); // Clear error message when user types
  };

  const handleResend = async () => {
    // Handle resend OTP logic
    try {
      const data = {
        email: email,
      };
      const otpResponse = await postData({
        route: "api/method/airport_transport.api.user.send_confirmation_email",
        data,
      });
      // Redirect to OTP verification screen
      if (otpResponse?.status === 200) {
        setTimer(getNewTime()); // Reset timer
        setShowResend(false); // Hide resend button
        const msg = otpResponse?.data?.msg?.toLowerCase();
        if (msg === "otp sent") {
          setOtp("");
          setError(""); // Clear error message when user types
          message.success(t("otp_sent"));
        } else {
          message.success(`${msg}`);
        }
      }
    } catch (error) {
      message.error(`${error?.response?.data?.msg}`);
    }
  };

  useEffect(() => {
    let interval = null;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else {
      setShowResend(true); // Show resend button when timer completes
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleVerify = useCallback(async () => {
    if (sendingRequest) {
      return;
    }
    try {
      const data = {
        email: email,
        otp: otp,
      };
      const response = await postData({
        route: "api/method/airport_transport.api.user.confirm_email",
        data,
      });
      if (response?.status === 200) {
        const msg = response?.data?.msg.toLowerCase();
        if (msg === "email confirmation successful") {
          setError(""); // Clear error message when user types
          message.success(t("email_confirmation_successful"));
        } else {
          message.success(`${msg}`);
        }
        resetExpiration();
        finishOTP(otp);
      }
      else{ RegisterTracking(false, "Invalid OTP"); }
    } catch (error) {}
  }, [otp]);
  useEffect(() => {
    if (otp.length === 6) {
      handleVerify();
    }
  }, [otp, handleVerify]);

  useEffect(() => {
    if (errors) {
      const errorMsg = errors?.error?.toLowerCase();
      if (errorMsg === "expired otp") {
        message.error(t("errors.expired_otp"));
        setError(t("errors.expired_otp"));
      } else if (errorMsg === "invalid otp") {
        message.error(t("errors.invalid_otp"));
        setError(t("errors.invalid_otp"));
      } else if (errorMsg === "'otp' is required") {
        setError(t("errors.otp_required"));
      } else {
        message.error(`${errorMsg}`);
        setError(`${errorMsg}`);
      }
    }
  }, [errors]);

  return (
    <>
      <div className="w-auto md:w-[400px] text-left rtl:text-right p-4">
        <Heading
          title={t("otp_verification_text")}
          className={"text-2xl text-[#0E0E11]"}
        />
        <div className="w-auto md:w-3/4">
          <p className="text-sm text-[#5D5D5D]">
            {t("enter_6_digit_text")}
            <span className="ml-1 text-text_steel_blue">{email}</span>{" "}
            <button
              className=" text-text_steel_blue underline font-bold cursor-pointer ml-1"
              onClick={unsetEmail}
            >
              ({t("change_text")})
            </button>
          </p>
        </div>
      </div>
      <div>
        <div className="p-2 md:p-4">
          <OtpInput
            inputStyle="otp-input"
            value={otp}
            onChange={handleChange}
            numInputs={6}
            renderInput={(props, i) => <input {...props} key={i} />}
          />
          {error && (
            <div className="text-base text-text_warning font-semibold my-2">
              {error}
            </div>
          )}

          <div className="mt-2 flex flex-row justify-start items-center">
            <div className="mr-1">
              {showResend ? (
                <Button
                  className="bg-background_steel_blue w-20 text-center text-text_white hover:bg-gray-100 font-medium rounded text-sm px-1 py-2 me-2 mb-2"
                  label={t("resend_text")}
                  type="button"
                  onClick={handleResend}
                />
              ) : (
                `${t("hero.resend_otp_text")} - ${Math.floor(timer / 60)
                  .toString()
                  .padStart(2, "0")}:${(timer % 60)
                  .toString()
                  .padStart(2, "0")}`
              )}
            </div>
          </div>
          <div className="my-3">
            <Button
              className="bg-background_steel_blue w-full text-text_white hover:bg-gray-100 font-medium rounded text-sm px-5 py-2.5 me-2 mb-2 mt-6"
              label={t("verify_text")}
              type="submit"
              onClick={handleVerify}
              disabled={sendingRequest}
            />
          </div>

          {/* <div className="text-sm text-background_steel_blue cursor-pointer">
            {t("problems_text")}
          </div> */}
        </div>
      </div>
    </>
  );
};

export default OTPStepForm;
